import ControlViewModel from '@/ViewModels/ControlViewModel';
import GridViewModel from '@/ViewModels/Controls/GridViewModel';
import CoreFrameGridViewModelT from '@/ViewModels/Controls/CoreFrameGridViewModel';
import MessageBoxViewModelT from '@/ViewModels/Controls/MessageBoxViewModel';
import ComponentManager from '@/Management/ComponentManager';
import RADApplication from '@/Management/RADApplication';
import ResourceManager from '@/Management/ResourceManager';
import { GridColumnDefinition } from "#/DefaultViewExtensionInterfaces";
import DetailViewContext from "@/ViewModels/Contexts/DetailViewContext";
import { IControlViewModelConstructorParameter } from "#/CoreFrameInterfaces";

interface ILDAPUsersViewModelConstructorParameters extends IControlViewModelConstructorParameter {
    detailViewContext?: DetailViewContext;
    username?: ObservableOrDefault<string>;
    firstName?: ObservableOrDefault<string>;
    lastName?: ObservableOrDefault<string>;
    email?: ObservableOrDefault<string>;
    company?: ObservableOrDefault<string>;
    language?: ObservableOrDefault<string>;
    extension1?: ObservableOrDefault<string>;
    extension2?: ObservableOrDefault<string>;
    extension3?: ObservableOrDefault<string>;
    memberOf?: ObservableOrDefault<string>;
    search?: ObservableOrDefault<any>;
    reset?: ObservableOrDefault<any>;
}

export default class LDAPUsersViewModel extends ControlViewModel {
    /**
     * Der DetailViewContext
     */
    public detailViewContext: DetailViewContext;

    /**
     * Der Benutzername
     */
    public username: KnockoutObservable<string>;

    /**
     * Der Vorname
     */
    public firstName: KnockoutObservable<string>;

    /**
     * Der Nachname
     */
    public lastName: KnockoutObservable<string>;

    /**
     * Die Emailadresse
     */
    public email: KnockoutObservable<string>;

    /**
     * Das ViewModel für das Grid
     * @type {GridViewModel}
     */
    public grid: GridViewModel;

    /**
     * Ein Observable zum Starten der Suche
     */
    public onSearch: KnockoutObservable<any>;

    /**
     * Ein Observable zum Zurücksetzen der Ergebnisse
     */
    public onReset: KnockoutObservable<any>;

    /**
     * Die Company
     */
    public company: KnockoutObservable<string>;

    /**
     * Die Sprache des Nutzers
     */
    public language: KnockoutObservable<string>;

    /**
     * Die Extensionfelder
     */
    public extension1: KnockoutObservable<string>;
    public extension2: KnockoutObservable<string>;
    public extension3: KnockoutObservable<string>;

    /**
     * Die Gruppenzuordnung
     */
    public memberOf: KnockoutObservable<string>;

    private CoreFrameGridViewModel = ComponentManager.getViewModel<typeof CoreFrameGridViewModelT>('CoreFrameGridViewModel');
    private MessageBoxViewModel = ComponentManager.getViewModel<typeof MessageBoxViewModelT>('MessageBoxViewModel');

    constructor(params: ILDAPUsersViewModelConstructorParameters) {
        super(params);

        this.detailViewContext = this.paramOrDefault(params.detailViewContext, null);
        this.undisposeProperties.push('detailViewContext');

        this.username = this.observableOrDefault(params.username, null);
        this.firstName = this.observableOrDefault(params.firstName, null);
        this.lastName = this.observableOrDefault(params.lastName, null);
        this.email = this.observableOrDefault(params.email, null);
        this.company = this.observableOrDefault(params.company, null);
        this.language = this.observableOrDefault(params.language, null);
        this.extension1 = this.observableOrDefault(params.extension1, null);
        this.extension2 = this.observableOrDefault(params.extension2, null);
        this.extension3 = this.observableOrDefault(params.extension3, null);
        this.memberOf = this.observableOrDefault(params.memberOf, null);
        this.onSearch = this.observableOrDefault(params.search, null);
        this.onReset = this.observableOrDefault(params.reset, null);

        this.grid = this.createGrid();

        this.defineSubscription(this.onSearch, () => this.search());
        this.defineSubscription(this.onReset, () => this.reset());
    }

    protected createGrid(): GridViewModel {
        return new this.CoreFrameGridViewModel({
            parentLocator: this.locator,
            locator: 'grid',
            columnDefinition: this.createGridColumns(),
            identity: 'id',
            pageSizes: [100],
            pageSize: 100,
            page: 1,
            orderBy: [{dir: 'ASC', field: 'lastName', index: 0}],
            onDoubleClick: (id, rowData, openInNewWindow) => {
                this.grid.selected([id]);
            },
            onSortChangedHandler: this.onSortChange.bind(this),
            hasSummaryRow: true
        });
    }

    protected createGridColumns(): GridColumnDefinition[] {
        return [
            this.createGridColumn('id', undefined, false, false, false),
            this.createGridColumn('userName', 'LDAP_LOGIN'),
            this.createGridColumn('firstName', 'LDAP_FIRSTNAME'),
            this.createGridColumn('lastName', 'LDAP_LASTNAME'),
            this.createGridColumn('email', 'EMAIL'),
            this.createGridColumn('company', 'LDAP_COMPANY'),
            this.createGridColumn('language', 'LDAP_LANGUAGE'),
            this.createGridColumn('extension1', 'RAD.RADFOUNDATION.PROBLEMDOMAIN.LDAP.RADLDAPCONNECTION_EXTENSION1ATTRIBUTE_ERGNAME'),
            this.createGridColumn('extension2', 'RAD.RADFOUNDATION.PROBLEMDOMAIN.LDAP.RADLDAPCONNECTION_EXTENSION2ATTRIBUTE_ERGNAME'),
            this.createGridColumn('extension3', 'RAD.RADFOUNDATION.PROBLEMDOMAIN.LDAP.RADLDAPCONNECTION_EXTENSION3ATTRIBUTE_ERGNAME'),
            this.createGridColumn('memberOf', 'LDAP_MEMBEROF', true, false, false),
            this.createGridColumn('origin', 'LDAP_ORIGIN'),
        ];
    }

    protected createGridColumn(name: string, ergName?: string, sortable = true, filterable = false, visible = true): GridColumnDefinition {
        const column: GridColumnDefinition = {
            name,
            ergName,
            field: undefined,
            sortable,
            isFilterable: filterable,
            isVisible: visible
        };

        if (sortable)
            column.sortValue = name;

        return column;
    }

    /**
     * Sucht nach LDAP Benutzern und aktualisiert das DataGrid
     */
    public search() {
        $(document.activeElement).blur();

        this.grid.data([]);
        this.grid.selected([]);
        this.grid.disabled([]);

        const username = this.username() || '';
        const firstName = this.firstName() || '';
        const lastName = this.lastName() || '';
        const email = this.email() || '';
        const company = this.company() || '';
        const language = this.language() || '';
        const extension1 = this.extension1() || '';
        const extension2 = this.extension2() || '';
        const extension3 = this.extension3() || '';
        const memberOf = this.memberOf() || '';

        const allParameters = [username, firstName, lastName, email, company, language, extension1, extension2, extension3, memberOf];
        if (allParameters.filter(p => !!p).length == 0) {
            RADApplication.getInstance().showMessageBox(this.MessageBoxViewModel.createNoticeMessageBox(null, 'NO_FILTER_PARAMS'));
            return;
        }

        if (allParameters.some(param => param.startsWith("*") && param !== "*")) {
            const message = ResourceManager.getInstance().get('LDAP_ENDSWITH_SEARCH_NOT_SUPPORTED');
            RADApplication.getInstance().showMessageBox(this.MessageBoxViewModel.createWarningMessageBox(message));
            return;
        }

        this.grid.increaseBusyCounter();
        this.detailViewContext.callOperationUnchecked('SearchExtended', allParameters).then((operationResult) => {
            if (!this.validateStatus(operationResult))
                return;

            const result = [];
            const selected = [];
            const disabled = [];
            const Root = JSON.parse(operationResult.returnValue);
            if (Root != null && Root.length != 0) {
                for (let i = 0; i < Root.length; i++) {
                    const user = Root[i];
                    this.pushUser(result, selected, disabled, user);
                }
            }
            this.grid.data(result);
            this.grid.totalRows(Root.length);
            this.grid.realTotalRows(Root.length);
            this.grid.selected(selected);
            this.grid.disabled(disabled);
            this.onSortChange(this.grid.orderBy());
        }).always(() => {
            this.grid.decreaseBusyCounter();
        });
    }

    /**
     * Setzt die Eingabewerte und die Suchergebnisse zurück
     */
    public reset() {
        $(document.activeElement).blur();
        this.username('');
        this.firstName('');
        this.lastName('');
        this.email('');
        this.company('');
        this.language('');
        this.extension1('');
        this.extension2('');
        this.extension3('');
        this.memberOf('');
        this.grid.data([]);
    }

    /**
     * Importiert die ausgewählten Benutzer
     */
    public import(mode?: 'named' | 'light'): void {
        const data = this.grid.data();
        const selected = this.grid.selected();
        if (selected.length > 0) {
            const usersToImport = [];
            for (let i = 0; i < data.length; i++) {
                const user = data[i];
                if (selected.indexOf(user.id) > -1) {
                    usersToImport.push({
                        Login: !user.userName ? '' : user.userName,
                        Firstname: !user.firstName ? '' : user.firstName,
                        Lastname: !user.lastName ? '' : user.lastName,
                        Company: !user.company ? '' : user.company,
                        Email: !user.email ? '' : user.email,
                        Language: !user.language ? '' : user.language,
                        Extension1: !user.extension1 ? '' : user.extension1,
                        Extension2: !user.extension2 ? '' : user.extension2,
                        Extension3: !user.extension3 ? '' : user.extension3,
                        MemberOf: !user.memberOf ? '' : user.memberOf,
                        AzureID: !user.azureId ? '' : user.azureId,
                        ConnectionPDOID: !user.connectionPDOId ? '' : user.connectionPDOId
                    });
                }
            }
            if (usersToImport.length > 0) {
                const json = JSON.stringify({ Entries: usersToImport, Mode: mode ?? null });
                const operationParameters = [json];
                this.increaseBusyCounter();
                this.detailViewContext.callOperationUnchecked('ImportUsers', operationParameters).then((operationResult) => {
                    if (!this.validateStatus(operationResult))
                        return;

                    this.detailViewContext.genericObject.isModified(true);
                    this.detailViewContext.apply();
                    const Root = JSON.parse(operationResult.returnValue);
                    let notImportedUserNames = Root != null && Root.NotImportedUsers != null ? Root.NotImportedUsers : [];
                    let importedUserNames = Root != null && Root.ImportedUsers != null ? Root.ImportedUsers : [];
                    if (typeof notImportedUserNames == 'string') {
                        notImportedUserNames = [notImportedUserNames];
                    }
                    if (typeof importedUserNames == 'string') {
                        importedUserNames = [importedUserNames];
                    }
                    if (notImportedUserNames.length > 0 && importedUserNames.length > 0) {
                        const msgBox = this.MessageBoxViewModel.createNoticeMessageBox(null, null, null, null, [{
                            text: ResourceManager.getInstance().get('LDAP_SUCCESSFUL_IMPORT'),
                            list: importedUserNames
                        }, {text: ResourceManager.getInstance().get('LDAP_UNSUCCESSFUL_IMPORT'), list: notImportedUserNames}]);
                        RADApplication.getInstance().showMessageBox(msgBox);
                    } else if (notImportedUserNames.length === 0 && importedUserNames.length > 0) {
                        const msgBox = this.MessageBoxViewModel.createNoticeMessageBox(ResourceManager.getInstance().get('LDAP_SUCCESSFUL_IMPORT'));
                        msgBox.textList = importedUserNames;
                        RADApplication.getInstance().showMessageBox(msgBox);
                    } else {
                        const msgBox = this.MessageBoxViewModel.createNoticeMessageBox(ResourceManager.getInstance().get('LDAP_UNSUCCESSFUL_IMPORT'));
                        msgBox.textList = notImportedUserNames;
                        RADApplication.getInstance().showMessageBox(msgBox);
                    }
                    this.search();
                }).always(() => {
                    this.decreaseBusyCounter();
                });
            }
        }
    }

    private onSortChange(sortExpression: any): any {
        this.increaseBusyCounter();
        try {
            const expression = sortExpression[0];
            const dir = expression.dir;
            const value = expression.field;
            this.grid.data.sort((first, second) => {
                const firstValue = first[value].toLowerCase();
                const secondValue = second[value].toLowerCase();
                if (firstValue === secondValue)
                    return 0;
                if (firstValue === '' || secondValue === '')
                    return firstValue > secondValue ? -1 : 1;
                return firstValue < secondValue ? -1 : 1;
            });
            if (dir === 'DESC')
                this.grid.data(this.grid.data.reverse()());
        } catch (ex) {
            throw ex;
        } finally {
            this.decreaseBusyCounter();
        }
    }

    /**
     * Fügt einen gesuchten Benutzer in entsprechende Arrays ein
     */
    private pushUser(result: any, selected: any, disabled: any, user: any): any {
        result.push({
            id: user.Login,
            isImported: user.IsImported,
            userName: user.Login,
            firstName: user.Firstname,
            lastName: user.Lastname,
            company: user.Company,
            email: user.Email,
            language: user.Language,
            extension1: user.Extension1,
            extension2: user.Extension2,
            extension3: user.Extension3,
            memberOf: user.MemberOf,
            origin: user.Origin,
            azureId: user.AzureID,
            connectionPDOId: user.ConnectionPDOID
        });
        if (user.IsImported) {
            disabled.push(user.Login);
            selected.push(user.Login);
        }
    }
}

ComponentManager.setViewModel('LDAPUsersViewModel', LDAPUsersViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');